/**
 * Google Tag Manager integration (via vue-gtm).
 *
 * SDK loading: NPM `vue-gtm`, registered with `Vue.use(VueGtm, ...)` in
 *              src/main.js:72-79. Plugin loads the GTM script when
 *              VUE_APP_TAG_MANAGER_ENABLED === 'true'.
 * Global:      Vue.prototype.$gtm  (vue-gtm plugin instance)
 * Env vars:    VUE_APP_TAG_MANAGER         (container ID, e.g. GTM-XXXXXX)
 *              VUE_APP_TAG_MANAGER_ENABLED ('true' / 'false')
 *              VUE_APP_DEBUG               ('true' / 'false', plugin debug)
 *
 * Status: ACTIVE — primary tag manager / GA4 pipeline across all sites.
 *
 * Existing call sites (as of 2026-04-13):
 *   - src/main.js:5,72-79                Vue.use(VueGtm, { ... })
 *   - src/utilities/commons.js:426-441   custom_event() pushes events via
 *                                        Vue.prototype.$gtm.trackEvent(payload)
 *
 * The `custom_event(action, label, value, category)` helper supports two
 * payload styles:
 *   1. Standard:  ('myAction', 'myLabel', 12, 'myCategory')
 *      → fires a `customEvent` GTM event with action/label/value/category.
 *   2. GA4 raw:   passing a full GA4 ecommerce object as `action`
 *      → forwarded as-is to GTM (already a GA4 event payload).
 *
 * This module wraps that exact behavior, plus a passthrough helper.
 *
 * Naming convention: every public function is prefixed with `gtm_` so the
 * GTM origin is obvious at every call site, even when imported alongside
 * other integration modules.
 */

import Vue from 'vue'

export function gtm_is_available() {
  return Boolean(Vue.prototype.$gtm)
}

/**
 * Push an event payload to GTM dataLayer.
 * Mirrors the existing logic in commons.js custom_event().
 *
 * @param {string|Object} action   Event name OR a complete GA4 event object.
 * @param {string} [label]         Ignored when `action` is an object.
 * @param {number} [value]         Ignored when `action` is an object.
 * @param {string} [category]      Ignored when `action` is an object.
 */
export function gtm_track_event(action, label, value, category) {
  if (!gtm_is_available()) return

  const safe_label = label || ''
  const safe_value = value || 0
  const safe_category = category || ''

  let payload
  if (typeof action === 'object') {
    // GA4 ecommerce-style payload, forward as-is
    payload = action
  } else {
    payload = {
      event: 'customEvent',
      category: safe_category ? safe_category : 'ce',
      action: action,
      label: safe_label,
      value: safe_value,
    }
  }

  Vue.prototype.$gtm.trackEvent(payload)
}

/**
 * Push a raw payload directly via vue-gtm (escape hatch for callers that
 * already have a GA4-shaped object and don't want the wrapper logic).
 */
export function gtm_push_raw(payload) {
  if (!gtm_is_available()) return
  if (!payload) return
  Vue.prototype.$gtm.trackEvent(payload)
}
